import {
  Answers,
  CONTACT_FIELD_KEYS,
  ContactInput,
  FormSubmitPayload,
  LeadInput,
  randomString,
} from '@jlg-website/shared';
import { ContactFilledOut, LeadCreated, QuestionsFilledOut } from '@/hooks/contact/events';
import { enqueue } from '@/lib/marketing/on_interaction/gtm';
import type useGoogleConversion from '@/hooks/useGoogleConversion';
import { compute as computeScore } from './score';

type MarketingConversion = ReturnType<typeof useGoogleConversion>;

// The contact is still empty when the visitor first reaches the contact step.
const EMPTY_CONTACT: ContactInput = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  zip_code: '',
  details: null,
  consent: false,
};

function sessionId(): string {
  return window.JLG?.analytics?.session?.session_id ?? '';
}

// Campaign attribution for the Google conversion events. Ids come from the marketing
// bootstrap; the lead-type name and score baseline mirror the contact form's defaults.
function marketingCampaign() {
  return {
    id: window.JLG?.marketing?.campaign?.id ?? 0,
    lead_type_id: window.JLG?.marketing?.lead_type?.id ?? 0,
    offer_id: window.JLG?.marketing?.offer?.id ?? '',
    lead_type_name: window.JLG?.marketing?.lead_type?.name ?? '',
    lead_score_baseline: window.JLG?.marketing?.lead_score_baseline ?? 0,
  };
}

// Coerces the engine's answers to the `LeadInput` shape: each value becomes a non-empty
// array and each score defaults to `[0]`, matching what the lead endpoint stores. Blank
// answers are dropped. Mirrors the server's `collectAnswers`.
export function toLeadAnswers(answers: Answers): LeadInput['lead']['answers'] {
  const coerced: LeadInput['lead']['answers'] = {};
  for (const [questionId, answer] of Object.entries(answers)) {
    const value = Array.isArray(answer.value)
      ? answer.value
      : answer.value === null || answer.value === undefined
        ? []
        : [answer.value];
    if (value.length === 0) continue;
    coerced[questionId] = { value, score: answer.score.length > 0 ? answer.score : [0] };
  }

  return coerced;
}

// The analytics events (`hooks/contact/events`) are typed on the full `LeadInput` but only
// read `lead.answers` and `lead.contact`. We build a complete, valid `LeadInput` here — the
// remaining fields are placeholders the events never read — so this stays confined to the
// form component without reshaping the shared event.
function toLeadEventInput(
  answers: Answers,
  contact: ContactInput,
  isQualified: boolean
): LeadInput {
  return {
    session: sessionId(),
    form: { id: 0 },
    campaign: {
      id: window.JLG?.marketing?.campaign?.id ?? 0,
      lead_type: {
        id: window.JLG?.marketing?.lead_type?.id ?? 0,
        lead_type_name: window.JLG?.marketing?.lead_type?.name ?? '',
      },
      lead_source: { id: window.JLG?.marketing?.lead_source?.id ?? 0 },
      lead_score_baseline: window.JLG?.marketing?.lead_score_baseline ?? 0,
      offer: { id: window.JLG?.marketing?.offer?.id ?? 0 },
    },
    lead: {
      answers: toLeadAnswers(answers),
      contact,
      is_qualified: isQualified,
      is_duplicate: false,
      score: computeScore(answers),
    },
    is_2fa_enabled: false,
    is_schedule_callback_enabled: false,
    analytics: { ip_address: null, user_agent: null, url: null },
  };
}

function pushGaEvent(event: string, attrs: Record<string, unknown> = {}): void {
  enqueue({
    event,
    session_id: window.JLG?.analytics?.session?.session_id ?? '',
    visitor_id: window.JLG?.analytics?.session?.visitor_id ?? '',
    ...attrs,
  });
}

/**
 * Emits a GA progress event once per step as the visitor advances (never on submit),
 * mirroring the `form_progress_*` events the contact form pushes in useContact. `emitted`
 * holds the steps already reported so each fires at most once.
 */
export function emitStepProgress(emitted: Set<number>, payload: FormSubmitPayload): void {
  if (emitted.has(payload.current_step)) return;
  emitted.add(payload.current_step);

  pushGaEvent(`form_progress_${payload.current_step}`);
  pushGaEvent('formStep', {
    event: 'formStep',
    oid: window.JLG?.marketing?.offer?.id ?? 0,
    cid: window.JLG?.marketing?.campaign?.id ?? 0,
    ltid: window.JLG?.marketing?.lead_type?.id ?? 0,
    step: payload.current_step,
    lead_value: computeScore(payload.answers),
    form_response: payload.answers,
    eventDedupId: randomString(16),
    version: '2',
  });
}

/** Emits the GA `form_submit` event as the visitor submits, mirroring useContact. */
export function emitFormSubmit(): void {
  pushGaEvent('form_submit');
}

/**
 * Flattens the submit payload into the question answers keyed by question id, dropping the
 * fixed contact fields but keeping questions interleaved into the contact block. Mirrors the
 * server's `collectAnswers` split, letting a single-step form report its questions before
 * the lead is submitted.
 */
export function collectQuestionAnswers(payload: FormSubmitPayload): Answers {
  const answers: Answers = {};
  const contactFieldKeys = CONTACT_FIELD_KEYS as readonly string[];

  for (const [key, answer] of Object.entries(payload.answers)) {
    if (contactFieldKeys.includes(key)) continue;
    answers[key] = answer;
  }

  return answers;
}

/**
 * Fired every time the visitor reaches the last (contact) step of a multi-step form: the
 * questions are done, so we push the "questions filled out" analytics event and the
 * CompleteRegistration conversion with all the answers gathered so far.
 */
export function emitQuestionsFilledOut(
  conversion: MarketingConversion,
  payload: FormSubmitPayload
): void {
  QuestionsFilledOut(toLeadEventInput(payload.answers, EMPTY_CONTACT, payload.is_qualified));
  conversion.sendCompleteRegistrationEvent(sessionId(), marketingCampaign(), payload);
}

/**
 * Fired after the lead is persisted, in order: the contact is captured (ContactFilledOut),
 * the lead row is acknowledged (LeadCreated) and the Lead conversion is sent. Uses the
 * answers and contact the server actually stored.
 */
export function emitLeadSubmitted(
  conversion: MarketingConversion,
  leadId: number,
  answers: Answers,
  contact: ContactInput,
  isQualified: boolean
): void {
  const eventInput = toLeadEventInput(answers, contact, isQualified);

  ContactFilledOut(eventInput);
  LeadCreated(leadId, eventInput);
  conversion.sendLeadEvent(sessionId(), marketingCampaign(), answers, contact);
}
